import type { LogExportMode, LogExportOptions, LogExportSort } from '@bcube/shared';
import { useMutation } from '@tanstack/react-query';
import { Download, Loader } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const MODE_OPTIONS: { value: LogExportMode; label: string; description: string }[] = [
  {
    value: 'latest_success',
    label: 'Último sucesso de cada dispositivo',
    description:
      'Uma linha por dispositivo, com o último registro que deu certo. Dispositivos que só tiveram falhas ficam de fora.',
  },
  {
    value: 'latest',
    label: 'Último registro de cada dispositivo',
    description: 'Uma linha por dispositivo, com a última tentativa, seja sucesso ou falha.',
  },
  {
    value: 'latest_failed',
    label: 'Dispositivos com falha pendente',
    description: 'Uma linha por dispositivo cuja última tentativa falhou.',
  },
  {
    value: 'all',
    label: 'Todos os registros (histórico completo)',
    description: 'Todas as tentativas. O mesmo dispositivo pode aparecer em várias linhas.',
  },
];

const SORT_OPTIONS: { value: LogExportSort; label: string }[] = [
  { value: 'desc', label: 'Mais recentes primeiro' },
  { value: 'asc', label: 'Mais antigos primeiro' },
];

const DEFAULT_OPTIONS: Required<LogExportOptions> = {
  mode: 'latest_success',
  sort: 'desc',
  include_attempts: true,
};

interface LogExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ex.: "identificação" ou "configuração" — aparece no título. */
  logLabel: string;
  /** Prefixo do arquivo baixado (ex.: "export.identification-log"). */
  fileNamePrefix: string;
  /** Resumo dos filtros da tela que também valem para a exportação. */
  activeFilters?: string[];
  exportFn: (options: Required<LogExportOptions>) => Promise<Blob>;
}

const formatDateTime = (date: Date) =>
  date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Texto do filtro de período para o resumo de filtros do modal. */
export function describeDateRangeFilter(from?: Date, to?: Date): string | undefined {
  if (from && to) return `Período: ${formatDateTime(from)} até ${formatDateTime(to)}`;
  if (from) return `A partir de ${formatDateTime(from)}`;
  if (to) return `Até ${formatDateTime(to)}`;
  return undefined;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function LogExportDialog({
  open,
  onOpenChange,
  logLabel,
  fileNamePrefix,
  activeFilters = [],
  exportFn,
}: LogExportDialogProps) {
  const { toast } = useToast();
  const [options, setOptions] = useState<Required<LogExportOptions>>(DEFAULT_OPTIONS);

  // Sempre abre com o padrão (último sucesso de cada dispositivo)
  useEffect(() => {
    if (open) setOptions(DEFAULT_OPTIONS);
  }, [open]);

  const exportMutation = useMutation({
    mutationKey: ['export-logs', fileNamePrefix],
    mutationFn: async (selected: Required<LogExportOptions>) => {
      const blob = await exportFn(selected);
      downloadBlob(blob, `${fileNamePrefix}.${Date.now()}.xlsx`);
    },
    onSuccess: () => onOpenChange(false),
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Erro ao exportar',
        description: 'Não foi possível gerar a planilha. Tente novamente.',
      });
    },
  });

  const isExporting = exportMutation.isPending;
  const perDevice = options.mode !== 'all';

  return (
    <Dialog open={open} onOpenChange={(value) => !isExporting && onOpenChange(value)}>
      <DialogContent className="sm:max-w-136">
        <DialogHeader>
          <DialogTitle>Exportar logs de {logLabel}</DialogTitle>
          <DialogDescription>Escolha como as informações devem vir no Excel.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label>Quais registros?</Label>
            <RadioGroup
              value={options.mode}
              onValueChange={(mode) =>
                setOptions((prev) => ({ ...prev, mode: mode as LogExportMode }))
              }
              disabled={isExporting}
            >
              {MODE_OPTIONS.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={`export-mode-${option.value}`}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3 font-normal transition-colors hover:bg-gray-50',
                    options.mode === option.value && 'border-gray-900 bg-gray-50',
                  )}
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`export-mode-${option.value}`}
                    className="mt-0.5"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-gray-900">
                      {option.label}
                      {option.value === DEFAULT_OPTIONS.mode && (
                        <span className="ml-2 text-xs font-normal text-gray-500">(padrão)</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">{option.description}</span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Ordem das linhas</Label>
            <RadioGroup
              value={options.sort}
              onValueChange={(sort) =>
                setOptions((prev) => ({ ...prev, sort: sort as LogExportSort }))
              }
              className="grid-cols-2"
              disabled={isExporting}
            >
              {SORT_OPTIONS.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={`export-sort-${option.value}`}
                  className="flex cursor-pointer items-center gap-2 rounded-md border p-3 font-normal hover:bg-gray-50"
                >
                  <RadioGroupItem value={option.value} id={`export-sort-${option.value}`} />
                  {option.label}
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="export-include-attempts"
              checked={perDevice && options.include_attempts}
              disabled={!perDevice || isExporting}
              onCheckedChange={(checked) =>
                setOptions((prev) => ({ ...prev, include_attempts: checked === true }))
              }
              className="mt-0.5"
            />
            <Label
              htmlFor="export-include-attempts"
              className={cn('flex flex-col gap-0.5 font-normal', !perDevice && 'opacity-50')}
            >
              <span className="text-sm text-gray-900">Incluir coluna "Tentativas"</span>
              <span className="text-xs text-gray-500">
                Quantas vezes cada dispositivo passou pelo processo no período filtrado.
              </span>
            </Label>
          </div>

          <p className="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
            {activeFilters.length > 0 ? (
              <>
                Filtros da tela que também serão aplicados:{' '}
                <span className="font-medium text-gray-900">{activeFilters.join(' · ')}</span>
              </>
            ) : (
              'Nenhum filtro aplicado na tela: todo o histórico será considerado.'
            )}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancelar
          </Button>
          <Button
            className="bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => exportMutation.mutate(options)}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Exportando, aguarde...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Exportar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
